class BaseExpressPay {

  constructor() {
    this.debug = true;
  }

  getCountryId(postalCode, countryCode) {
    const countryCodeIdMap = { GB: 1, NI: 13, SH: 15, CI: 18 };
    return this.isChannelIslands(postalCode, countryCode) ? countryCodeIdMap.CI
      : this.isNorthernIreland(postalCode, countryCode) ? countryCodeIdMap.NI
      : this.isScottishHighlands(postalCode, countryCode) ? countryCodeIdMap.SH
      : countryCodeIdMap.GB;
  }

  isChannelIslands(postalCode, countryCode) {
    /*
      Returns true if the address is in the Channel Islands
    */
    if (countryCode !== "GB") return false;
    return postalCode.toLowerCase().startsWith("je") || postalCode.toLowerCase().startsWith("gy");
  }

  isScottishHighlands(postalCode, countryCode) {
    /*
      Returns true if the address is in the Scottish Highlands
    */
    if (countryCode !== "GB") return false;
    return /^HS|IV|KA27|KW|PA20|PA21|PA38|PH41|PH42|ZE/.test(postalCode.toUpperCase());
  }

  isNorthernIreland(postalCode, countryCode) {
    /*
      Returns true if the address is in Northern Ireland
    */
    if (countryCode !== "GB") return false;
    return postalCode.toLowerCase().startsWith("bt");
  }

  isIsleOfMan(postalCode, countryCode) {
    /*
      Returns true if the address is in the Isle of Man
    */
    if (countryCode !== "GB") return false;
    return postalCode.toLowerCase().startsWith("im");
  }

  getAvailableShippingOptions(postalCode, countryCode) {
    /* 
      Returns shipping options based on country and postal code
      Utilises the shippingOptions array defined in the constructor
      Returns: Array of available shipping options for the stripe payment request
    */
    let availableShippingOptions = [];
    (countryCode === "GB") && (availableShippingOptions = this.shippingOptions.filter(opt => opt.countries.some(c => c.id === "1")));
    this.isChannelIslands(postalCode, countryCode) && (availableShippingOptions = this.shippingOptions.filter(opt => opt.countries.some(c => c.id === "18")));
    this.isScottishHighlands(postalCode, countryCode) && (availableShippingOptions = this.shippingOptions.filter(opt => opt.countries.some(c => c.id === "15")));
    this.isNorthernIreland(postalCode, countryCode) && (availableShippingOptions = this.shippingOptions.filter(opt => opt.countries.some(c => c.id === "13")));
    return availableShippingOptions.map(opt => ({
      id: opt.id,
      amount: opt.amount,
      label: opt.label,
      detail: opt.detail,
      minimum: opt.leadTimeMin,
      maximum: opt.leadTime,
    }));
  }

  getFirstSelectedShippingOption(availableShippingOptions) {
    /*
      Returns the first shipping option from the available options
      This is used to set a default shipping option for the payment request
    */
    if (Array.isArray(availableShippingOptions) && availableShippingOptions.length > 0) {
      return availableShippingOptions[0];
    }
    return null;
  }

  validateShippingAddress(countryCode, countryId, postalCode) {
    /*
      Verifies if the shipping address is valid
      The country must be in the allowed list and match the postal code restrictions
      Returns: true if valid, false otherwise
    */
    if (!countryCode || !countryId || !postalCode) return false;
    if (countryId === 18 && this.isChannelIslands(postalCode, countryCode)) return true;
    if (countryId === 15 && this.isScottishHighlands(postalCode, countryCode)) return true;
    if (countryId === 13 && this.isNorthernIreland(postalCode, countryCode)) return true;
    if (countryCode === "GB" && countryId === 1) return true;
    return false;
  }

  splitName(fullName) {
    /*
      Splits a full name into first and last name
      If only one name is provided, it will be treated as the first name with an empty last name
      Returns: Object with firstName and lastName properties
    */
    if (!fullName) return { firstName: "", lastName: "" };
    const parts = fullName.trim().split(" ");
    return {
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" ") || "",
    };
  }

  async updateBasket(data) {
    const response = await fetch(`/express-checkout/basket/update/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify(data),
    });
    return await response.json();
  }

  async basketChecker(orderId, pvids) {
    const response = await fetch(`/basket/api/basket-checker/${orderId}/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify({ pvids: pvids }),
    });
    const result = await response.json();
    if (!result['happy_check']) {
      console.warn("Basket check failed:", result);
      return false;
    }
    if (result['order_processed']) {
      console.warn("Order has already been processed. Redirecting to confirmation page.");
      return false;
    }
    return true;
  }

  async setDeliveryInfoOnOrder(deliveryInfo) {
    const response = await fetch(`/basket/api/set-delivery-info/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify(deliveryInfo),
    });
    return response.ok;
  }

  async generatePaymentIntent(orderId, data, source='basket') {
    (data) && (data.source = source);
    const response = await fetch(
      `/basket/api/generate-payment-intent/${orderId}/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "X-CSRFToken": getCSRFToken(),
        },
        body: JSON.stringify(data),
      }
    );
    return await response.json();
  }

  async processOrder(data) {
    const response = await fetch(`/basket/api/process-order/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify(data),
    });
    return await response.text();
  }

}

export default BaseExpressPay;